/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { string, func } from 'prop-types';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withLayoutGroup } from '@thd-olt-component-react/layout-manager';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { QuantityAdjustmentsContext } from '@thd-olt-component-react/bundling-product-tabs';
import { useStoreId } from '@thd-nucleus/experience-context';
import { PackageHeader } from './subcomponents/PackageHeader';
import { PackageInfo } from './subcomponents/PackageInfo';
import { PackagesMiniContext } from './PackagesMiniContext';
import { MAJOR_APPLIANCE_TYPE } from '../constants';

import './packages-mini.scss';
import { dataModel } from './dataModel';
import { PackagesMiniPlaceholder } from './loader/packagesMiniLoader';

const determineIfMajorAppliancePackage = (apiResponsePackage) => {
  return apiResponsePackage?.products.every(
    (item) => item.identifiers.productType === MAJOR_APPLIANCE_TYPE
  );
};

const attachAlternateProductsAndExtendProductInfo = ({ newPackageModel, productGrouping, products }) => {
  // eslint-disable-next-line no-unused-expressions
  products?.forEach((item) => {
    let itemProductGroup = item.info.productGroup;
    const productGroup = productGrouping[itemProductGroup];
    const isSelected = productGroup?.default === item.identifiers.itemId;
    if (isSelected) newPackageModel.selectedProductIds.push(item.identifiers.itemId);
    newPackageModel.products.set(item.identifiers.itemId, item);
  });
  return newPackageModel;
};

export const organizeProductsByClassAndSubclass = (apiResponsePackage) => {
  const productGrouping = {};
  const newPackageModel = {
    metadata: { ...apiResponsePackage.metadata },
    products: new Map(),
    selectedProductIds: [],
    totalPricing: {},
    isMajorAppliancePackage: determineIfMajorAppliancePackage(apiResponsePackage)
  };
  /* eslint-disable-next-line no-unused-expressions */
  apiResponsePackage.products.forEach((item) => {
    let itemProductGroup = item.info.productGroup;
    const alternateProduct = {
      itemId: item.identifiers.itemId,
      price: item.pricing.value,
      hasMap: item.pricing.mapAboveOriginalPrice !== null,
      label: item.info.label
    };

    if (productGrouping[itemProductGroup]) {
      const productGroup = productGrouping[itemProductGroup];
      productGroup.products.push(alternateProduct);
    } else {
      productGrouping[itemProductGroup] = {
        products: [alternateProduct],
        default: item.identifiers.itemId
      };
    }
  });
  return attachAlternateProductsAndExtendProductInfo({
    newPackageModel,
    productGrouping,
    products: apiResponsePackage.products
  });
};

const getSelectedProducts = (newPackageModel) => {
  const {
    selectedProductIds: selectedIds
  } = newPackageModel;
  return selectedIds.reduce((acc, curr) => acc.concat(newPackageModel.products.get(curr)), []) || [];
};

const PackagesMiniComponent = ({ itemId, callOnRender }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('packages-mini.ready'); }, []);

  const storeId = useStoreId();
  let previousUrl = '';
  if (typeof window === 'object') {
    previousUrl = document.referrer;
  }
  const previousUrlMatch = previousUrl.match(/package_([a-zA-Z]|[0-9])*/);

  const { loading, data } = useDataModel('packages', {
    variables: {
      packageId: previousUrlMatch ? previousUrlMatch?.[0] : undefined,
      itemId: previousUrlMatch ? undefined : itemId,
      storeId,
      limit: 1
    },
    context: {
      withAuth: true
    },
    ssr: true
  });

  useEffect(() => {
    if (data?.packages?.[0]?.metadata
      && data?.packages?.[0]?.products) {
      callOnRender();
    }
  }, [data]);

  const [itemMap, setItemMap] = useState({});

  useEffect(() => {
    if (data?.packages?.[0]?.products) {
      setItemMap(data.packages[0].products.reduce((map, product) => {
        if (product?.itemId) {
          return {
            ...map,
            [product.itemId]: { ...map[product.itemId], selectedQty: 1 }
          };
        }
        return map;
      }, itemMap));
    }
  }, [data]);

  if (loading) {
    return (
      <PackagesMiniPlaceholder />
    );
  }

  if (!data?.packages?.[0]?.metadata || !data?.packages?.[0]?.products) {
    return null;
  }

  const newModel = organizeProductsByClassAndSubclass(data.packages[0]);
  const selectedProducts = getSelectedProducts(newModel);
  const packageCategory = data?.packages[0]?.metadata?.packageCategory;

  return (
    <div className="packages-mini" data-testid="packages-mini">
      <PackagesMiniContext.Provider value={{
        products: selectedProducts,
        newModel,
        packageCategory,
        anchorItemId: itemId,
      }}
      >
        <QuantityAdjustmentsContext.Provider value={{ itemMap, setItemMap }}>
          <PackageHeader />
          <PackageInfo />
        </QuantityAdjustmentsContext.Provider>
      </PackagesMiniContext.Provider>
    </div>
  );
};

const propTypes = {
  itemId: string,
  callOnRender: func
};

const defaultProps = {
  itemId: '',
  callOnRender: () => {},
};

PackagesMiniComponent.displayName = 'PackagesMini';
PackagesMiniComponent.propTypes = propTypes;
PackagesMiniComponent.defaultProps = defaultProps;
PackagesMiniComponent.dataModel = dataModel;

const HydratedComponent = withHydrator({
  id: 'product-section-packages-mini',
  scrollBuffer: 100,
  delay: 1500,
  preserveCtxVal: 'clientStore',
  placeholder: <PackagesMiniPlaceholder />
}, PackagesMiniComponent);

const DynamicComponent = withDynamicComponent(HydratedComponent);
const PackagesMini = withLayoutGroup({
  name: 'packages-mini'
}, DynamicComponent);
PackagesMini.propTypes = propTypes;
PackagesMini.defaultProps = defaultProps;
PackagesMini.dataModel = dataModel;
PackagesMini.displayName = 'PackagesMini';
export { PackagesMini };
